import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Printer, Share2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { OSDocument } from "@/components/site/OSDocument";
import { TextInput } from "@/components/site/Field";
import { WhatsAppIcon } from "@/components/site/WhatsAppFloat";
import { api, brl, errMsg } from "@/lib/api";
import { ACEITE, waLink } from "@/lib/content";
import { osWhatsappText } from "@/lib/whatsapp";

const AcceptPanel = ({ os, token, onDone }) => {
  const [nome, setNome] = useState("");
  const [doc, setDoc] = useState("");
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);
  if (os.aceite) return null;
  if (os.status !== "aguardando_aceite") {
    return (
      <div className="no-print mx-auto mt-8 max-w-4xl border border-amber-700/50 bg-amber-950/20 p-6 text-sm text-amber-200" data-testid="accept-unavailable">
        Aguardando a precificação final pelo prestador (custo de deslocamento e valor total). O aceite digital ficará disponível assim que o valor total for apresentado.
      </div>
    );
  }
  const accept = async () => {
    if (nome.trim().length < 3 || !ok) return toast.error("Informe seu nome completo e confirme a ciência dos termos");
    setBusy(true);
    try {
      const { data } = await api.post(`/os/share/${token}/accept`, { nome, documento: doc, concordo_termos: ok });
      onDone(data);
      toast.success("Aceite registrado com sucesso");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="no-print mx-auto mt-8 max-w-4xl border-2 border-red-700 bg-[#111723] p-6 sm:p-8" data-testid="accept-panel">
      <p className="eyebrow">{ACEITE.titulo}</p>
      <p className="mt-2 font-heading text-2xl font-bold text-white">Valor total: <span className="text-red-500" data-testid="accept-total">{brl(os.valores.total)}</span></p>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <TextInput label="Nome completo" required value={nome} onChange={setNome} testid="accept-nome" />
        <TextInput label="CPF / CNPJ (opcional)" value={doc} onChange={setDoc} testid="accept-documento" />
      </div>
      <label className="mt-5 flex cursor-pointer items-start gap-3 text-sm text-slate-300">
        <input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} className="mt-1 h-4 w-4 accent-red-600" data-testid="accept-termos-checkbox" />
        <span>Li e concordo com as condições contratuais e o termo de garantia. {ACEITE.advertencia}</span>
      </label>
      <button onClick={accept} disabled={busy} className="btn-red mt-6 w-full sm:w-auto" data-testid="quote-accept-digital-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} {ACEITE.botao}
      </button>
      <p className="mt-3 text-xs text-slate-500">Serão registrados: nome, data e hora e IP (quando tecnicamente disponível).</p>
    </div>
  );
};

export default function OSView() {
  const { token } = useParams();
  const [os, setOs] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/os/share/${token}`).then((r) => setOs(r.data)).catch((e) => setErr(errMsg(e)));
  }, [token]);

  if (err) return <p className="py-32 text-center text-slate-400" data-testid="os-not-found">{err}</p>;
  if (!os) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-red-500" /></div>;

  const link = window.location.href;
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: `OS ${os.numero} - Nillo Ruídos`, url: link }); return; } catch { return; }
    }
    await navigator.clipboard?.writeText(link);
    toast.success("Link da OS copiado");
  };

  return (
    <div className="bg-[#0B0F17] px-4 py-10 sm:px-6" data-testid="page-os-view">
      <div className="no-print mx-auto mb-6 flex max-w-4xl flex-wrap gap-3">
        <button onClick={() => window.print()} className="btn-red" data-testid="os-print-btn"><Printer className="h-4 w-4" /> Imprimir / Salvar PDF</button>
        <button onClick={share} className="btn-line" data-testid="os-share-btn"><Share2 className="h-4 w-4" /> Compartilhar</button>
        <a href={waLink(osWhatsappText(os, link))} target="_blank" rel="noreferrer" className="btn-line" data-testid="os-whatsapp-btn"><WhatsAppIcon className="h-4 w-4" /> Enviar ao WhatsApp</a>
      </div>
      <OSDocument os={os} />
      <AcceptPanel os={os} token={token} onDone={setOs} />
    </div>
  );
}
